import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, inputCls, labelCls } from "@/components/kit";
import CommuneField from "@/components/CommuneField";
import { CheckCircle2, ArrowRight } from "lucide-react";

const CSE_EVENT_TYPES = ["Arbre de Noël CSE", "Family Day / journée du personnel", "Soirée CSE", "Team building / sortie collective", "Autre événement CSE"];
const CSE_GUESTS = ["Moins de 50", "50 à 100", "100 à 200", "200 à 500", "Plus de 500"];
const CSE_BUDGETS = ["Moins de 3 000 €", "3 000 à 5 000 €", "5 000 à 10 000 €", "10 000 à 20 000 €", "Plus de 20 000 €", "Budget à définir"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function ChipGroup({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="group">
      {options.map((o) => {
        const active = value === o;
        return (
          <button type="button" key={o} aria-pressed={active} onClick={() => onChange(o)}
            data-testid={`${testid}-${o.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-lime bg-lime text-ink" : "border-ink/15 bg-white text-ink/60 hover:border-lime-dim hover:text-ink"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Formulaire dédié CSE — demande de proposition en 2 minutes.
 * Alimente le même pipeline admin (type devis, organisation « CSE »).
 */
export default function CSEForm({ city = "", cityCode = "", testid = "cse-form" }) {
  const [form, setForm] = useState({
    service: "", participants: "", budget: "", city, city_code: cityCode,
    event_date: "", company: "", name: "", email: "", phone: "", message: "",
  });
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) { toast.error("Choisissez un type d'événement."); return; }
    if (!form.name.trim()) { toast.error("Votre nom est requis."); return; }
    if (!EMAIL_RE.test(form.email)) { toast.error("Un email professionnel valide est requis."); return; }
    if (!form.phone.trim()) { toast.error("Un téléphone est requis pour vous rappeler."); return; }
    setLoading(true);
    try {
      const payload = { type: "devis", org_type: "CSE", ...form, source: getSource() };
      const { data } = await api.post("/submissions", payload);
      track("form_submit");
      setSent(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-lime-dim/40 bg-lime/10 p-8 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 size={34} className="mx-auto text-lime-dim" />
        <p className="mt-3 font-serif text-2xl text-ink">Demande bien reçue{sent.dossier ? ` — dossier ${sent.dossier}` : ""}.</p>
        <p className="mt-2 text-sm text-ink/60">Un chef de projet rappelle votre contact CSE sous 24 h ouvrées avec une première proposition.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-ink/10 bg-white p-8 md:p-10" data-testid={testid}>
      <p className="font-serif text-2xl text-ink">Demandez votre devis événement CSE</p>
      <p className="mt-2 text-sm text-ink/55">Deux minutes, sans long e-mail. Réponse sous 24 h ouvrées.</p>

      <div className="mt-7 space-y-7">
        <div>
          <p className={labelCls}>Type d'événement <span className="text-lime-dim">*</span></p>
          <ChipGroup options={CSE_EVENT_TYPES} value={form.service} onChange={(v) => set({ service: v })} testid={`${testid}-type`} />
        </div>
        <div>
          <p className={labelCls}>Nombre de participants</p>
          <ChipGroup options={CSE_GUESTS} value={form.participants} onChange={(v) => set({ participants: v })} testid={`${testid}-guests`} />
        </div>
        <div>
          <p className={labelCls}>Budget envisagé</p>
          <ChipGroup options={CSE_BUDGETS} value={form.budget} onChange={(v) => set({ budget: v })} testid={`${testid}-budget`} />
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor={`${testid}-city`} className={labelCls}>Ville de l'événement</label>
            <CommuneField id={`${testid}-city`} value={city} defaultCode={cityCode}
              placeholder="Commune — ex : Lyon, Échirolles, Valence…"
              onSelect={(v) => set(v)} />
          </div>
          <div>
            <label htmlFor={`${testid}-date`} className={labelCls}>Date envisagée</label>
            <input id={`${testid}-date`} type="date" className={inputCls} data-testid={`${testid}-date`}
              value={form.event_date} onChange={(e) => set({ event_date: e.target.value })} />
          </div>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor={`${testid}-company`} className={labelCls}>CSE / Entreprise</label>
            <input id={`${testid}-company`} type="text" className={inputCls} data-testid={`${testid}-company`}
              value={form.company} placeholder="CSE de…" onChange={(e) => set({ company: e.target.value })} />
          </div>
          <div>
            <label htmlFor={`${testid}-name`} className={labelCls}>Nom du contact <span className="text-lime-dim">*</span></label>
            <input id={`${testid}-name`} type="text" required className={inputCls} data-testid={`${testid}-name`}
              value={form.name} placeholder="Prénom Nom" onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div>
            <label htmlFor={`${testid}-email`} className={labelCls}>Email professionnel <span className="text-lime-dim">*</span></label>
            <input id={`${testid}-email`} type="email" required className={inputCls} data-testid={`${testid}-email`}
              value={form.email} placeholder="contact@entreprise.fr" onChange={(e) => set({ email: e.target.value })} />
          </div>
          <div>
            <label htmlFor={`${testid}-phone`} className={labelCls}>Téléphone <span className="text-lime-dim">*</span></label>
            <input id={`${testid}-phone`} type="tel" required className={inputCls} data-testid={`${testid}-phone`}
              value={form.phone} placeholder="06 12 34 56 78" onChange={(e) => set({ phone: e.target.value })} />
          </div>
        </div>
        <div>
          <label htmlFor={`${testid}-message`} className={labelCls}>Vos attentes (optionnel)</label>
          <textarea id={`${testid}-message`} rows={3} className={inputCls} data-testid={`${testid}-message`}
            value={form.message} placeholder="Âge des enfants, thème souhaité, contraintes particulières…"
            onChange={(e) => set({ message: e.target.value })} />
        </div>
      </div>

      <button type="submit" disabled={loading} onClick={() => track("click_devis")} className={`${btnPrimary} mt-8 w-full justify-center disabled:opacity-60`} data-testid={`${testid}-submit`}>
        {loading ? "Envoi…" : "Recevoir ma proposition CSE"} <ArrowRight size={16} />
      </button>
      <p className="mt-3 text-center text-xs text-ink/45">Un seul interlocuteur, du choix des animations à la coordination le jour J.</p>
    </form>
  );
}
